// HabitTab — daily habit tracker ported from Apps/Yawmi. Storage: aweni_yawmi_habits.
import type { AweniContext } from '../../types';
import { AthkarCounter } from './AthkarCounter';

interface Habit {
  id: string; name: string; icon: string; category: string;
  frequency: 'daily' | 'weekdays' | 'weekly' | 'custom'; days?: number[];
  targetCount: number; unit: string;
  streakCurrent: number; streakBest: number; createdAt: string;
}
interface HabitLog { habitId: string; date: string; count: number; }
interface YawmiWisdomItem {
  id: string;
  source: string;
  citation: string;
  arabic: string;
  translit: string;
  translation_en: string;
}
interface YawmiWisdomData {
  version: number;
  wisdoms: YawmiWisdomItem[];
}

const HABITS_KEY = 'aweni_yawmi_habits';
const LOGS_KEY = 'aweni_yawmi_habit_logs';
const ATHKAR_HABIT_PREFIX = 'p_athkar';
const YAWMI_WISDOM_URL = '/aweni/data/yawmi-wisdom.v1.json';
const YAWMI_WISDOM_ID = 'bukhari-6464';
let yawmiWisdomCache: YawmiWisdomItem[] | null = null;
let yawmiWisdomLoadPromise: Promise<YawmiWisdomItem[] | null> | null = null;

const PRESETS: Habit[] = [
  { id: 'p_fajr', name: 'yawmi_habits_preset_fajr_name', icon: '🕌', category: 'worship', frequency: 'daily', targetCount: 1, unit: '', streakCurrent: 0, streakBest: 0, createdAt: '' },
  { id: 'p_quran', name: 'yawmi_habits_preset_quran_pages_name', icon: '📖', category: 'knowledge', frequency: 'daily', targetCount: 3, unit: 'pages', streakCurrent: 0, streakBest: 0, createdAt: '' },
  { id: 'p_dhikr_m', name: 'yawmi_habits_preset_dhikr_morning_name', icon: '🌅', category: 'worship', frequency: 'daily', targetCount: 1, unit: '', streakCurrent: 0, streakBest: 0, createdAt: '' },
  { id: 'p_dhikr_e', name: 'yawmi_habits_preset_dhikr_evening_name', icon: '🌙', category: 'worship', frequency: 'daily', targetCount: 1, unit: '', streakCurrent: 0, streakBest: 0, createdAt: '' },
  { id: 'p_walk', name: 'yawmi_habits_preset_walk_name', icon: '🚶', category: 'health', frequency: 'daily', targetCount: 1, unit: '', streakCurrent: 0, streakBest: 0, createdAt: '' },
  { id: 'p_water', name: 'yawmi_habits_preset_water_name', icon: '💧', category: 'health', frequency: 'daily', targetCount: 8, unit: 'glasses', streakCurrent: 0, streakBest: 0, createdAt: '' },
  { id: 'p_sleep', name: 'yawmi_habits_preset_sleep_early_name', icon: '😴', category: 'health', frequency: 'daily', targetCount: 1, unit: '', streakCurrent: 0, streakBest: 0, createdAt: '' },
  { id: 'p_book', name: 'yawmi_habits_preset_read_book_name', icon: '📚', category: 'knowledge', frequency: 'daily', targetCount: 1, unit: '', streakCurrent: 0, streakBest: 0, createdAt: '' },
  { id: 'p_family', name: 'yawmi_habits_preset_family_call_name', icon: '📞', category: 'social', frequency: 'weekly', days: [0, 5, 6], targetCount: 1, unit: '', streakCurrent: 0, streakBest: 0, createdAt: '' },
  { id: 'p_journal', name: 'yawmi_habits_preset_journal_name', icon: '✏️', category: 'personal', frequency: 'daily', targetCount: 1, unit: '', streakCurrent: 0, streakBest: 0, createdAt: '' },
  { id: 'p_athkar_post', name: 'yawmi_habits_preset_athkar_post_name', icon: '📿', category: 'worship', frequency: 'daily', targetCount: 1, unit: '', streakCurrent: 0, streakBest: 0, createdAt: '' },
];

export class HabitTab {
  private el: HTMLElement;
  private habits: Habit[] = [];
  private logs: HabitLog[] = [];
  private view: 'tracker' | 'browse' | 'athkar' = 'tracker';
  private filterCat = 'all';
  private athkarCounter: AthkarCounter | null = null;
  private mounted = false;
  private wisdomLoadFailed = false;

  constructor(container: HTMLElement) { this.el = container; }

  mount(ctx: AweniContext): void {
    this.mounted = true;
    try { this.habits = JSON.parse(localStorage.getItem(HABITS_KEY) || '[]'); } catch { this.habits = []; }
    try { this.logs = JSON.parse(localStorage.getItem(LOGS_KEY) || '[]'); } catch { this.logs = []; }
    this.render(ctx);
  }

  unmount(): void {
    this.mounted = false;
    this.athkarCounter?.unmount();
    this.athkarCounter = null;
    this.el.innerHTML = '';
  }

  private tr(ctx: AweniContext, k: string, fb: string): string { return ctx.i18n.t(k) || fb; }
  private today(): string { return new Date().toISOString().slice(0, 10); }
  private todayCount(id: string): number { return this.logs.find(l => l.habitId === id && l.date === this.today())?.count ?? 0; }
  private isDone(h: Habit): boolean { return this.todayCount(h.id) >= h.targetCount; }
  private showToday(h: Habit): boolean {
    const dow = new Date().getDay();
    return h.frequency === 'daily' ? true : h.frequency === 'weekdays' ? dow >= 1 && dow <= 5 : (h.days ?? []).includes(dow);
  }

  /** Count consecutive recent days where this habit met its target. If today
   *  is not yet done, counts back from yesterday. Only supports daily
   *  frequency in v1 (returns 0 otherwise). Capped at 365 to bound the loop. */
  private computeCurrentStreak(h: Habit): number {
    if (h.frequency !== 'daily') return 0;
    let count = 0;
    const today = new Date();
    let i = this.isDone(h) ? 0 : 1;
    for (; i < 365; i++) {
      const d = new Date(today);
      d.setDate(today.getDate() - i);
      const dateStr = d.toISOString().slice(0, 10);
      const log = this.logs.find(l => l.habitId === h.id && l.date === dateStr);
      if (log && log.count >= h.targetCount) count++;
      else break;
    }
    return count;
  }

  /** Mascot streak milestone hook (T3.1 + T3.2 FLAGSHIP). For each visible
   *  daily habit, if its current streak hits a milestone (7 or 30) and we
   *  haven't already celebrated this habit/milestone today, dispatch the
   *  mascot appear event and stamp the dedupe key with today's date.
   *  Only the first matching habit fires per paint (anti-double-mascot). */
  private maybeFireStreakMascot(habits: Habit[]): void {
    const today = this.today();
    for (const h of habits) {
      if (h.frequency !== 'daily') continue;
      const streak = this.computeCurrentStreak(h);
      const milestone = streak === 7 ? 7 : streak === 30 ? 30 : 0;
      if (!milestone) continue;
      const key = `aweni_mascot_streak_${h.id}_${milestone}`;
      try { if (localStorage.getItem(key) === today) continue; } catch { /* ignore */ }
      try { localStorage.setItem(key, today); } catch { /* quota */ }
      const detail = milestone === 7
        ? { mascot: 'foxplorr', pose: 'cheer', surface: 'yawmi', reason: 'streak_7d',  bubbleKey: 'mascot_streak_7d' }
        : { mascot: 'foxplorr', pose: 'celebration', surface: 'yawmi', reason: 'streak_30d', bubbleKey: 'mascot_streak_30d' };
      try { window.dispatchEvent(new CustomEvent('aweni:mascot:appear', { detail })); } catch { /* SSR-safe */ }
      if (milestone === 30) {
        // Bigger fanfare for the month milestone — haptic burst (no-op on
        // unsupported devices). User-gesture context: the +1 button click
        // that triggered re-render is the implicit gesture.
        try { (navigator as Navigator & { vibrate?: (pattern: number[]) => boolean }).vibrate?.([60, 40, 60, 40, 120]); } catch { /* ignore */ }
      }
      return; // fire only the first matching milestone this paint
    }
  }

  private isValidWisdomItem(value: unknown): value is YawmiWisdomItem {
    if (!value || typeof value !== 'object') return false;
    const wisdom = value as Partial<YawmiWisdomItem>;
    return typeof wisdom.id === 'string'
      && typeof wisdom.source === 'string'
      && typeof wisdom.citation === 'string'
      && typeof wisdom.arabic === 'string'
      && typeof wisdom.translit === 'string'
      && typeof wisdom.translation_en === 'string';
  }

  private loadWisdoms(ctx: AweniContext): void {
    if (yawmiWisdomCache || yawmiWisdomLoadPromise || this.wisdomLoadFailed) return;
    yawmiWisdomLoadPromise = fetch(YAWMI_WISDOM_URL, { cache: 'default' })
      .then(async res => {
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const data = await res.json() as Partial<YawmiWisdomData>;
        const wisdoms = Array.isArray(data.wisdoms) ? data.wisdoms.filter(item => this.isValidWisdomItem(item)) : [];
        yawmiWisdomCache = wisdoms;
        return yawmiWisdomCache;
      })
      .catch((error: unknown) => {
        this.wisdomLoadFailed = true;
        console.warn('Failed to load Yawmi wisdom data', error);
        yawmiWisdomCache = null;
        return null;
      })
      .finally(() => {
        yawmiWisdomLoadPromise = null;
        if (this.mounted && this.view === 'tracker') this.render(ctx);
      });
  }

  private renderWisdomSkeleton(ctx: AweniContext): string {
    const isAr = (ctx.i18n.locale || 'en') === 'ar';
    return `
      <aside class="ht-wisdom" aria-label="${ctx.i18n.t('yawmi_wisdom_aria') || 'Spiritual anchor for daily habits'}"
        aria-live="polite" aria-busy="true"
        style="margin-bottom:16px;padding:14px 16px;
          background:var(--ln-bg-card);border-radius:var(--ln-radius-md);
          border-${isAr ? 'right' : 'left'}:3px solid var(--ln-border);
          box-shadow:var(--ln-shadow-sm)">
        <div style="height:14px;background:var(--ln-border);border-radius:6px;opacity:.45;margin-bottom:8px;width:100%"></div>
        <div style="height:10px;background:var(--ln-border);border-radius:6px;opacity:.35;margin-bottom:6px;width:78%"></div>
        <div style="height:12px;background:var(--ln-border);border-radius:6px;opacity:.3;width:58%"></div>
      </aside>`;
  }

  private renderWisdomStrip(ctx: AweniContext): string {
    if (this.wisdomLoadFailed) return '';
    if (!yawmiWisdomCache) {
      this.loadWisdoms(ctx);
      return this.renderWisdomSkeleton(ctx);
    }

    const wisdom = yawmiWisdomCache.find(item => item.id === YAWMI_WISDOM_ID) ?? yawmiWisdomCache[0];
    if (!wisdom) return '';

    const translationLabel = ctx.i18n.t('hadith_translation_label') || 'Translation';
    const isAr = (ctx.i18n.locale || 'en') === 'ar';
    // Keep English canonical wording until certified locale-specific translations
    // are added to the shared JSON payload.

    return `
      <aside class="ht-wisdom" aria-label="${ctx.i18n.t('yawmi_wisdom_aria') || 'Spiritual anchor for daily habits'}"
        style="margin-bottom:16px;padding:14px 16px;position:relative;
          background:var(--ln-bg-card);border-radius:var(--ln-radius-md);
          border-${isAr ? 'right' : 'left'}:3px solid var(--ln-accent);
          box-shadow:var(--ln-shadow-sm);
          background-image:url('/aweni/patterns/geometric-8fold.webp');
          background-size:120px;background-repeat:repeat;
          background-blend-mode:multiply">
        <style>.ht-wisdom { background-color: var(--ln-bg-card); } .ht-wisdom::before { content: ''; position: absolute; inset: 0; background: var(--ln-bg-card); opacity: 0.92; border-radius: inherit; pointer-events: none; z-index: 0; } .ht-wisdom > * { position: relative; z-index: 1; }</style>
        <div lang="ar" dir="rtl" style="font-family:var(--ln-font-arabic,'IBM Plex Sans Arabic',serif);
          font-size:1.05rem;line-height:1.85;color:var(--ln-text-primary);margin-bottom:6px">
          ${wisdom.arabic}
        </div>
        <div style="font-size:.72rem;font-style:italic;color:var(--ln-text-secondary);
          margin-bottom:4px;letter-spacing:.01em">
          ${wisdom.translit}
        </div>
        <div style="font-size:.85rem;color:var(--ln-text-primary);line-height:1.5;margin-bottom:6px">
          ${translationLabel}: ${wisdom.translation_en}
        </div>
        <div style="font-size:.7rem;color:var(--ln-text-secondary);font-weight:500">
          — ${wisdom.citation}
        </div>
      </aside>`;
  }

  private save(): void {
    localStorage.setItem(HABITS_KEY, JSON.stringify(this.habits));
    const cutoff = new Date(); cutoff.setDate(cutoff.getDate() - 120);
    this.logs = this.logs.filter(l => l.date >= cutoff.toISOString().slice(0, 10));
    localStorage.setItem(LOGS_KEY, JSON.stringify(this.logs));
  }

  private increment(id: string): void {
    const existing = this.logs.find(l => l.habitId === id && l.date === this.today());
    if (existing) { existing.count++; } else { this.logs.push({ habitId: id, date: this.today(), count: 1 }); }
    this.save();
  }

  private delete(id: string): void {
    this.habits = this.habits.filter(h => h.id !== id);
    this.logs = this.logs.filter(l => l.habitId !== id);
    this.save();
  }

  private addPreset(preset: Habit): void {
    if (this.habits.some(h => h.id === preset.id)) return;
    this.habits.push({ ...preset, createdAt: new Date().toISOString() });
    this.save();
  }

  private render(ctx: AweniContext): void {
    const t = (k: string, fb: string) => this.tr(ctx, k, fb);
    const todayHabits = this.habits.filter(h => this.showToday(h));
    const done = todayHabits.filter(h => this.isDone(h)).length;

    if (this.view === 'browse') { this.renderBrowse(ctx); return; }
    if (this.view === 'athkar') { this.renderAthkar(ctx); return; }

    const habitRows = todayHabits.map(h => {
      const count = this.todayCount(h.id);
      const pct = Math.min(100, Math.round((count / h.targetCount) * 100));
      const done_ = count >= h.targetCount;
      const isAthkar = h.id.startsWith(ATHKAR_HABIT_PREFIX);
      return `
        <div class="ht-row" data-id="${h.id}" style="display:flex;align-items:center;gap:12px;padding:12px;
          background:var(--ln-bg-card);border-radius:var(--ln-radius-md);margin-bottom:8px;
          opacity:${done_ ? '.65' : '1'}">
          <span style="font-size:1.4rem">${h.icon}</span>
          <div style="flex:1;min-width:0">
            <div style="font-weight:600;font-size:.9rem;white-space:nowrap;overflow:hidden;text-overflow:ellipsis">
              ${t(h.name, h.name)}
            </div>
            <div style="height:4px;background:var(--ln-border,rgba(0,0,0,.1));border-radius:2px;margin-top:4px;overflow:hidden">
              <div style="height:4px;width:100%;background:var(--ln-accent);border-radius:2px;transform:scaleX(${pct/100});transform-origin:left;transition:transform var(--motion-duration-slow,300ms) var(--motion-easing-standard,ease)"></div>
            </div>
            <div style="font-size:.75rem;color:var(--ln-text-secondary);margin-top:2px">${count}/${h.targetCount}${h.unit ? ' ' + h.unit : ''}</div>
          </div>
          ${done_ ? `<span style="color:var(--ln-accent);font-size:1.2rem">✓</span>` : isAthkar ? `
            <button class="ht-athkar" data-id="${h.id}" style="padding:6px 10px;background:var(--ln-accent);color:var(--ln-accent-text);
              border:none;border-radius:var(--ln-radius-sm);cursor:pointer;font-weight:700;font-size:.8rem">📿</button>
          ` : `
            <button class="ht-inc" data-id="${h.id}" style="padding:6px 10px;background:var(--ln-accent);color:var(--ln-accent-text);
              border:none;border-radius:var(--ln-radius-sm);cursor:pointer;font-weight:700">+1</button>
          `}
          <button class="ht-del" data-id="${h.id}" style="padding:4px 8px;background:transparent;border:1px solid var(--ln-border);
            border-radius:var(--ln-radius-sm);cursor:pointer;font-size:.75rem;color:var(--ln-text-secondary)">×</button>
        </div>`;
    }).join('');

    this.el.innerHTML = `
      <div style="padding:0 0 16px">
        ${this.renderWisdomStrip(ctx)}
        <div style="display:flex;justify-content:space-between;align-items:center;margin-bottom:12px">
          <div>
            <div style="font-size:1.1rem;font-weight:700">${done}/${todayHabits.length} ${t('yawmi_habits_today', 'habits today')}</div>
            <div style="height:6px;background:var(--ln-border);border-radius:3px;margin-top:4px;width:160px">
              <div style="height:6px;background:var(--ln-accent);border-radius:3px;width:${todayHabits.length ? Math.round(done/todayHabits.length*100) : 0}%"></div>
            </div>
          </div>
          <button id="ht-browse-btn" style="padding:8px 14px;background:var(--ln-accent);color:var(--ln-accent-text);
            border:none;border-radius:var(--ln-radius-full);cursor:pointer;font-size:.85rem;font-weight:700">
            + ${t('yawmi_habits_add', 'Add Habit')}
          </button>
        </div>
        ${habitRows || `
          <div style="text-align:center;padding:20px 0 40px">
            <picture>
              <source srcset="/aweni/illustrations/yawmi-empty-state.webp" type="image/webp">
              <img src="/aweni/illustrations/yawmi-empty-state.png"
                alt=""
                aria-hidden="true"
                loading="lazy"
                width="320" height="240"
                style="max-width:min(320px,80vw);height:auto;display:block;margin:0 auto 12px;opacity:.92" />
            </picture>
            <p style="color:var(--ln-text-secondary);font-size:.95rem;margin:0 0 4px">${t('yawmi_habits_empty', 'No habits for today.')}</p>
            <p style="color:var(--ln-text-secondary);font-size:.8rem;margin:0;opacity:.7">${t('yawmi_habits_empty_hint', 'Tap + Add Habit to start your journey')}</p>
          </div>`}
      </div>`;

    this.el.querySelectorAll<HTMLButtonElement>('.ht-inc').forEach(btn => {
      btn.addEventListener('click', () => {
        this.increment(btn.dataset.id!);
        this.render(ctx);
      });
    });
    this.el.querySelectorAll<HTMLButtonElement>('.ht-athkar').forEach(btn => {
      btn.addEventListener('click', () => {
        this.view = 'athkar';
        this.render(ctx);
      });
    });
    this.el.querySelectorAll<HTMLButtonElement>('.ht-del').forEach(btn => {
      btn.addEventListener('click', () => {
        if (confirm(t('yawmi_habits_delete_confirm', 'Delete this habit?'))) {
          this.delete(btn.dataset.id!);
          this.render(ctx);
        }
      });
    });
    this.el.querySelector('#ht-browse-btn')?.addEventListener('click', () => {
      this.view = 'browse';
      this.render(ctx);
    });

    // Streak milestone mascot (FLAGSHIP). Defer to next frame so the DOM
    // settles before the mascot lands — avoids visual collision with the
    // habit row's progress-bar transition.
    requestAnimationFrame(() => this.maybeFireStreakMascot(todayHabits));
  }

  private renderBrowse(ctx: AweniContext): void {
    const t = (k: string, fb: string) => this.tr(ctx, k, fb);
    const cats = ['all', 'worship', 'health', 'knowledge', 'social', 'personal'];
    const filtered = this.filterCat === 'all' ? PRESETS : PRESETS.filter(p => p.category === this.filterCat);

    this.el.innerHTML = `
      <div>
        <div style="display:flex;justify-content:space-between;align-items:center;margin-bottom:12px">
          <strong>${t('yawmi_habits_browse_title', 'Add Habits')}</strong>
          <button id="ht-back" style="padding:6px 12px;background:transparent;border:1px solid var(--ln-border);
            border-radius:var(--ln-radius-full);cursor:pointer;font-size:.85rem">${t('yawmi_habits_back', 'Back')}</button>
        </div>
        <div style="display:flex;gap:6px;flex-wrap:wrap;margin-bottom:12px">
          ${cats.map(c => `<button class="ht-cat-btn" data-cat="${c}" style="padding:4px 10px;border-radius:var(--ln-radius-full);
            border:1px solid var(--ln-border);background:${c === this.filterCat ? 'var(--ln-accent)' : 'var(--ln-bg-card)'};
            color:${c === this.filterCat ? 'var(--ln-accent-text)' : 'inherit'};cursor:pointer;font-size:.8rem">
            ${t('yawmi_habits_cat_' + c, c)}</button>`).join('')}
        </div>
        ${filtered.map(p => {
          const added = this.habits.some(h => h.id === p.id);
          return `<div style="display:flex;align-items:center;gap:10px;padding:10px;background:var(--ln-bg-card);
            border-radius:var(--ln-radius-md);margin-bottom:6px">
            <span style="font-size:1.3rem">${p.icon}</span>
            <span style="flex:1;font-size:.9rem">${t(p.name, p.name)}</span>
            <button class="ht-add-preset" data-id="${p.id}" ${added ? 'disabled' : ''} style="padding:5px 10px;
              background:${added ? 'var(--ln-border)' : 'var(--ln-accent)'};color:${added ? 'var(--ln-text-secondary)' : 'var(--ln-accent-text)'};
              border:none;border-radius:var(--ln-radius-sm);cursor:${added ? 'default' : 'pointer'};font-size:.8rem">
              ${added ? t('yawmi_habits_already_added', 'Added') : '+ ' + t('yawmi_habits_added', 'Add')}</button>
          </div>`;
        }).join('')}
      </div>`;

    this.el.querySelector('#ht-back')?.addEventListener('click', () => { this.view = 'tracker'; this.render(ctx); });
    this.el.querySelectorAll<HTMLButtonElement>('.ht-cat-btn').forEach(btn => {
      btn.addEventListener('click', () => { this.filterCat = btn.dataset.cat!; this.renderBrowse(ctx); });
    });
    this.el.querySelectorAll<HTMLButtonElement>('.ht-add-preset').forEach(btn => {
      btn.addEventListener('click', () => {
        const preset = PRESETS.find(p => p.id === btn.dataset.id);
        if (preset) { this.addPreset(preset); this.renderBrowse(ctx); }
      });
    });
  }

  private renderAthkar(ctx: AweniContext): void {
    const t = (k: string, fb: string) => this.tr(ctx, k, fb);
    this.athkarCounter?.unmount();
    this.athkarCounter = null;

    this.el.innerHTML = `
      <div>
        <div style="display:flex;align-items:center;gap:10px;margin-bottom:16px">
          <button id="ht-athkar-back" style="padding:6px 12px;background:transparent;
            border:1px solid var(--ln-border);border-radius:var(--ln-radius-full);
            cursor:pointer;font-size:.85rem">← ${t('yawmi_habits_back', 'Back')}</button>
          <strong>${t('yawmi_athkar_title', 'Post-Prayer Adhkar')}</strong>
        </div>
        <div id="ht-athkar-container"></div>
      </div>`;

    this.el.querySelector('#ht-athkar-back')?.addEventListener('click', () => {
      this.athkarCounter?.unmount();
      this.athkarCounter = null;
      this.view = 'tracker';
      this.render(ctx);
    });

    const container = this.el.querySelector<HTMLElement>('#ht-athkar-container');
    if (container) {
      this.athkarCounter = new AthkarCounter(container, () => {
        // All dhikr done — mark the athkar habit complete for today
        const athkarHabit = this.habits.find(h => h.id.startsWith(ATHKAR_HABIT_PREFIX));
        if (athkarHabit && !this.isDone(athkarHabit)) {
          this.increment(athkarHabit.id);
        }
      });
      this.athkarCounter.mount(ctx);
    }
  }
}
